import Spinner from '@rio-cloud/rio-uikit/Spinner';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useAppDispatch, useAppSelector } from '../../../../../configuration/setup/hooks';
import { deleteAssociation } from '../../../actions/assets/Assets.actions';
import {
    getAssets,
    getFetchingSelectedDevice,
    getSelectedAssetId,
    getSelectedAssociatedDevices,
    openEasyOnboardingDialog,
} from '../../../reducers/assets/assetsSlice';
import TelematicsName from '../TelematicsName';
import { type Asset, AssetStatus, AssetType, type AssociatedDevice, type Device } from '../types';
import { DeviceListDeletionButton } from './DeviceListDeletionButton';
import { DeviceListDeletionDialog } from './DeviceListDeletionDialog';

export const DeviceList = () => {
    const dispatch = useAppDispatch();

    const assets = useAppSelector(getAssets);
    const selectedAssetId = useAppSelector(getSelectedAssetId);
    const fetchingDevices = useAppSelector(getFetchingSelectedDevice);
    const associatedDevices = useAppSelector(getSelectedAssociatedDevices);

    const [displayDeviceDeleteDialog, setDisplayDeviceDeleteDialog] = useState<boolean>(false);
    const [devicesToDisassociate, setDevicesToDisassociate] = useState<Device[]>([]);
    const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);

    useEffect(() => {
        if (selectedAssetId !== null) {
            setSelectedAsset(assets.find(asset => asset.id === selectedAssetId) ?? null);
        } else {
            setSelectedAsset(null);
        }
    }, [assets, selectedAssetId]);

    const associateDevice = () => {
        // (selected) asset is always set when the device list tab is open
        if (selectedAssetId !== null) {
            dispatch(openEasyOnboardingDialog(selectedAssetId));
        }
    };

    const abortDelete = () => {
        closeDialogAndClearDevice();
    };

    const closeDialogAndClearDevice = () => {
        setDisplayDeviceDeleteDialog(false);
        setDevicesToDisassociate([]);
    };

    const disassociateDevices = (devices: Device[]) => () => {
        for (const device of devices) {
            const assocToDelete = {
                // biome-ignore lint/style/noNonNullAssertion: suppress out of convenience during migration to biome
                id: associatedDevices.find(associatedDevice => associatedDevice.device.id === device.id)!.associationId,
                device_id: device.id,
                // biome-ignore lint/style/noNonNullAssertion: suppress out of convenience during migration to biome
                asset_id: selectedAssetId!,
            };
            void dispatch(deleteAssociation(assocToDelete, device.type));
            closeDialogAndClearDevice();
        }
    };

    const onDisassociationButtonClick = (devices: Device[]) => {
        setDisplayDeviceDeleteDialog(true);
        setDevicesToDisassociate(devices);
    };

    const deviceList =
        associatedDevices.length > 0 ? (
            <div>
                <table className='table table-condensed' data-cy='detail-view-data-sources'>
                    <thead>
                        <tr>
                            <th className='padding-left-0 width-45pct'>
                                <FormattedMessage id={'assets.assets.asset.devices.device.type'} />
                            </th>
                            <th className='width-45pct'>
                                <FormattedMessage id={'assets.assets.asset.devices.device.identification'} />
                            </th>
                            {<th className='width-10pct' />}
                        </tr>
                    </thead>
                    <tbody>
                        {associatedDevices.map((associatedDevice: AssociatedDevice) => (
                            <tr key={associatedDevice.device.id}>
                                <td className='padding-left-0 user-select-all'>
                                    <TelematicsName deviceType={associatedDevice.device.type} />
                                </td>
                                <td className='user-select-all' data-cy='device-identification'>
                                    {associatedDevice.device.identification}
                                </td>
                                <td className='disassociate-device'>
                                    <DeviceListDeletionButton
                                        associatedDevice={associatedDevice}
                                        allAssociatedDevices={associatedDevices}
                                        onDisassociationButtonClick={onDisassociationButtonClick}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <DeviceListDeletionDialog
                    devicesToDisassociate={devicesToDisassociate}
                    displayDeviceDeleteDialog={displayDeviceDeleteDialog}
                    disassociateDevices={disassociateDevices}
                    abortDelete={abortDelete}
                />
            </div>
        ) : (
            <div className={'padding-top-25 padding-bottom-15 text-center'}>
                {selectedAsset?.type !== AssetType.trailer ? (
                    <span>{<FormattedMessage id={'assets.assets.create.device-connect-message.details'} />}</span>
                ) : (
                    <div />
                )}
            </div>
        );

    const archivedAssetMessage = (
        <>
            <div className={'padding-top-25 padding-bottom-15 text-center'}>
                <FormattedMessage id={'assets.assets.create.device-connect-message.archived-asset'} />
            </div>
            <div className={'padding-top-25 padding-bottom-25 text-center'}>
                <FormattedMessage id={'assets.assets.create.device-connect-message.activate-asset-to-connect-device'} />
            </div>
        </>
    );

    const associateDeviceButton = (
        <div className='table-toolbar-column text-center padding-top-25 padding-bottom-15'>
            <button
                className='btn btn-link btn-link-inline'
                type='button'
                id='associateDevice'
                data-cy='associate-device'
                onClick={associateDevice}
            >
                <span className='rioglyph rioglyph-plus' />
                <FormattedMessage id={'assets.assets.create.device-connect-message.connect'} />
            </button>
        </div>
    );

    const sidebarTabContent =
        selectedAsset?.status === AssetStatus.archived ? (
            archivedAssetMessage
        ) : (
            <div>
                {deviceList}
                {selectedAsset?.type !== AssetType.trailer ? associateDeviceButton : <div />}
            </div>
        );

    return (
        <div>
            {fetchingDevices ? (
                <Spinner text={<FormattedMessage id={'assets.spinner.loading'} />} />
            ) : (
                sidebarTabContent
            )}
        </div>
    );
};
